import { useNavigate } from "react-router"
import Header from "@components/Header/Header"
import { useState, useCallback, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import {
    Container,
    ContentContainer,
    AddressContainer,
    ButtonsContainer,
    ResetWalletContainer,
    Divider,
    ResetWalletText,
    ResetWalletBottomSheetBodyContainer,
    ResetWalletWarningText,
    StyledAddressDisplay
} from './Settings.styles'

import BottomSheet from "@components/BottomSheet/BottomSheet"
import MenuItem from "@components/MenuItem/MenuItem"
import BottomSheetHeader from "@components/BottomSheetHeader/BottomSheetHeader"

import CopyButton from "@components/CopyButton/CopyButton"
import QRButton from "@components/QRButton/QRButton"
import PrimaryButton from "@components/PrimaryButton/PrimaryButton"
import SectionLabel from "@components/SectionLabel/SectionLabel"
import { useTranslation } from "react-i18next"
import VersionNumber from "@components/VersionNumber/VersionNumber"
import usePublicAddress from "@hooks/usePublicAddress"
import SkeletonLoader from "@components/SkeletonLoader/SkeletonLoader"
import useKYCDetail from "@hooks/useKYCDetail"

import { setHeaderColor } from "@lib/telegram"
import { WHITE } from "@styles/colors"
import { useUserRestrictions } from "@hooks/useUserRestrictions"

const ResetWalletButton = ({ onClick }: { onClick: () => void }) => {
    const { t } = useTranslation()
    return (
        <ResetWalletContainer>
            <Divider />
            <ResetWalletText onClick={onClick}>{t('page.settings.resetWallet.buttonText')}</ResetWalletText>
            <Divider />
        </ResetWalletContainer>
    )
}

const ResetWalletBottomSheetBody = () => {
    const navigate = useNavigate()
    const { logout } = useAuth()
    const { t } = useTranslation()

    const handleResetWallet = async () => {
        // Perform logout to clear authentication state
        await logout()
        // Navigate to splash/welcome screen
        navigate('/')
    }

    return (
        <ResetWalletBottomSheetBodyContainer>
            <ResetWalletWarningText>
                {t('page.settings.resetWallet.warning')}
            </ResetWalletWarningText>
            <PrimaryButton
                onClick={handleResetWallet}
                variant='danger'
            >
                {t('page.settings.resetWallet.confirmButton')}
            </PrimaryButton>
        </ResetWalletBottomSheetBodyContainer>
    )
}

const Settings = () => {
    const { data: kycDetail } = useKYCDetail()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const publicAddress = usePublicAddress()
    const [resetOpen, setResetOpen] = useState(false)
    const { canExportPrivateKey, canRedeemToBank, canRedeemToEwallet, canTransfer } = useUserRestrictions()

    useEffect(() => {
        setHeaderColor(WHITE)
    }, [])

    const handleResetOpen = useCallback(() => {
        setResetOpen(true)
    }, [])

    const handleResetClose = useCallback(() => {
        setResetOpen(false)
    }, [])

    return (
        <Container>
            <Header title={t('page.settings.header')} showBackButton={true} onBackClick={() => navigate('/home')} />
            <ContentContainer>
                <SectionLabel>{t('page.settings.sections.walletAddress')}</SectionLabel>

                <AddressContainer>

                    {publicAddress ?
                        <StyledAddressDisplay
                            address={publicAddress}
                            numberOfFirstCharacters={9}
                            numberOfLastCharacters={10}
                        />
                        :
                        <SkeletonLoader width="70%" />
                    }

                    <ButtonsContainer>
                        <CopyButton stringToCopy={publicAddress} onClick={() => { }} />
                        <QRButton onClick={() => navigate('/qr-scanner', { state: { from: '/settings', to: '/manage-address?mode=add' } })} />
                    </ButtonsContainer>
                </AddressContainer>

                <SectionLabel>{t('page.settings.sections.advanceOptions')}</SectionLabel>

                <MenuItem
                    title={t('page.settings.menuItems.advance.title')}
                    description={t('page.settings.menuItems.advance.description')}
                    onClick={() => navigate('/advance-options')}
                />

                <SectionLabel>{t('page.settings.sections.editInformation')}</SectionLabel>

                <MenuItem
                    title={t('page.settings.menuItems.account.title')}
                    description={t('page.settings.menuItems.account.description')}
                    onClick={() => navigate('/account')}
                />

                <MenuItem
                    title={t('page.settings.menuItems.security.title')}
                    description={t('page.settings.menuItems.security.description')}
                    onClick={() => navigate('/security')}
                />

                {(canRedeemToBank() || canRedeemToEwallet() || canTransfer()) && <MenuItem
                    title={t('page.settings.menuItems.bankAndWhitelist.title')}
                    description={t('page.settings.menuItems.bankAndWhitelist.description')}
                    onClick={() => navigate('/bank-and-address')}
                />}

                {canExportPrivateKey() && (
                    <MenuItem
                        title={t('page.settings.menuItems.recoveryPhrase.title')}
                        description={t('page.settings.menuItems.recoveryPhrase.description')}
                        onClick={
                            () =>
                                navigate('/authentication', {
                                    state: {
                                        operationType: 'recovery-phrase',
                                        headerTitle: t('page.settings.menuItems.recoveryPhrase.headerTitle'),
                                        description: t('page.settings.menuItems.recoveryPhrase.authDescription'),
                                        useBiometrics: true
                                    }
                                })
                        }
                    />
                )}

                <MenuItem
                    title={t('page.settings.menuItems.language.title')}
                    description={t('page.settings.menuItems.language.description')}
                    onClick={() => navigate('/language')}
                />

                {(kycDetail?.kyc_level === 'kyc-level-1' &&
                    kycDetail?.review_status === 'APPROVED') &&
                    <MenuItem
                        title={t('page.settings.menuItems.referralCode.title')}
                        description={t('page.settings.menuItems.referralCode.description')}
                        onClick={() => navigate('/referral-code')}
                    />
                }

                <MenuItem
                    title={t('page.settings.menuItems.legalCompliance.title')}
                    description={t('page.settings.menuItems.legalCompliance.description')}
                    onClick={() => navigate('/legal-compliance')}
                />

                {/* {process.env.NODE_ENV === 'development' && <VersionNumber />} */}
                <VersionNumber />

                <ResetWalletButton onClick={handleResetOpen} />

            </ContentContainer>

            {/* Reset Wallet Bottom Sheet */}
            <BottomSheet
                isOpen={resetOpen}
                onClose={handleResetClose}
                title={t('page.settings.resetWallet.title')}
                height="270px"
                header={
                    <BottomSheetHeader title={t('page.settings.resetWallet.headerTitle')} uppercase={true} />
                }
            >
                <ResetWalletBottomSheetBody />
            </BottomSheet>
        </Container>
    )
}

export default Settings